import type { WindowConfiguration } from '@termovista/window-configurator'

export const CONFIGURABLE_DOOR_TYPES = ['door', 'entrance_door'] as const
export const DOOR_OPENING_TYPES = ['fixed', 'turn'] as const
export type DoorAccessorySnapshot = {
  schemaVersion: 1
  sourceDoorAccessoryId: string
  code: string
  type: 'pull_handle'
  model: string
  color: string
  sizeMm: number
}
export type DoorConfiguration = {
  infillType: 'glass' | 'panel'
  lockRequired: boolean
  panelReference: string | null
  accessoryId: string | null
  accessory: DoorAccessorySnapshot | null
}
export type DoorWriteInput = Partial<Pick<DoorConfiguration, 'infillType' | 'lockRequired' | 'panelReference' | 'accessoryId'>>
export function isConfigurableDoorType(type: unknown) { return type === 'door' || type === 'entrance_door' }
export function emptyDoor(): DoorConfiguration {
  return { infillType: 'glass', lockRequired: false, panelReference: null, accessoryId: null, accessory: null }
}
export function normalizeDoor(value?: DoorWriteInput, current?: DoorConfiguration): DoorConfiguration {
  if (value !== undefined && (!value || typeof value !== 'object' || Array.isArray(value))) throw new Error('Door configuration is invalid.')
  if (value && Object.keys(value).some(key => !['infillType', 'lockRequired', 'panelReference', 'accessoryId'].includes(key))) throw new Error('Door accessory snapshots are managed by the server.')
  const door = { ...emptyDoor(), ...current, ...value }
  if (!['glass', 'panel'].includes(door.infillType)) throw new Error('Choose glass or panel infill.')
  if (typeof door.lockRequired !== 'boolean') throw new Error('Door lock selection must be a boolean.')
  if (door.infillType === 'glass') return { ...door, panelReference: null, accessoryId: null, accessory: null }
  door.panelReference = typeof door.panelReference === 'string' ? door.panelReference.trim() || null : null
  if (!door.panelReference) throw new Error('Enter the door panel reference.')
  if (!door.accessoryId || !/^rec[a-zA-Z0-9]{14}$/.test(door.accessoryId)) throw new Error('Select a door pull handle.')
  if (door.accessoryId !== current?.accessoryId) door.accessory = null
  return door
}
export function validDoorAccessory(value: unknown): value is DoorAccessorySnapshot {
  if (!value || typeof value !== 'object') return false
  const a = value as DoorAccessorySnapshot
  return a.schemaVersion === 1 && /^rec[a-zA-Z0-9]{14}$/.test(a.sourceDoorAccessoryId)
    && a.type === 'pull_handle' && [a.code, a.model, a.color].every(v => typeof v === 'string' && Boolean(v.trim()))
    && Number.isInteger(a.sizeMm) && a.sizeMm > 0
}
export function readDoorAccessorySnapshot(value: unknown): DoorAccessorySnapshot | null {
  try {
    const a: unknown = typeof value === 'string' ? JSON.parse(value) : value
    if (!validDoorAccessory(a)) return null
    // Whitelist the persisted contract; never carry unknown catalog metadata.
    return { schemaVersion: 1, sourceDoorAccessoryId: a.sourceDoorAccessoryId, code: a.code, type: a.type, model: a.model, color: a.color, sizeMm: a.sizeMm }
  } catch { return null }
}
export function accessoryFromRecord(record: Record<string, unknown>): DoorAccessorySnapshot | null {
  return readDoorAccessorySnapshot({ schemaVersion: 1, sourceDoorAccessoryId: record.id, code: record.code, type: record.type, model: record.model, color: record.color, sizeMm: record.sizeMm })
}
export function readDoor(record: Record<string, unknown>, accessory?: DoorAccessorySnapshot | null): DoorConfiguration {
  const snapshot = accessory === undefined ? readDoorAccessorySnapshot(record.doorAccessorySnapshotJson) : accessory
  const panel = record.doorInfillType === 'panel'
  return {
    infillType: panel ? 'panel' : 'glass', lockRequired: record.doorLockRequired === true,
    panelReference: panel && typeof record.doorPanelReference === 'string' ? record.doorPanelReference : null,
    accessoryId: panel ? (typeof record.doorAccessoryId === 'string' ? record.doorAccessoryId : snapshot?.sourceDoorAccessoryId ?? null) : null,
    accessory: panel ? snapshot : null,
  }
}
export function doorValid(door: DoorConfiguration, requireSnapshot = true) {
  try { normalizeDoor(undefined, door); return door.infillType === 'glass' || !requireSnapshot || validDoorAccessory(door.accessory) } catch { return false }
}
export function validateDoorWindowConfiguration(configuration: WindowConfiguration): string[] {
  const panels = configuration.panels
  const errors: string[] = []
  if (panels.some(panel => !DOOR_OPENING_TYPES.includes(panel.openingType as 'fixed' | 'turn'))) errors.push('Doors allow fixed and turn panels only. Correct the existing configuration before saving or requesting an RFQ.')
  if (!panels.some(panel => panel.openingType === 'turn')) errors.push('A door must have at least one turn panel.')
  return errors
}
export function doorSummary(door: DoorConfiguration) {
  return [door.infillType === 'glass' ? 'Glass door' : `Panel door: ${door.panelReference ?? 'not specified'}`, `Lock: ${door.lockRequired ? 'Yes' : 'No'}`, door.accessory ? `${door.accessory.model} · ${door.accessory.color} · ${door.accessory.sizeMm} mm` : null].filter(Boolean).join(' · ')
}
